import { Box, Flex, Icon, List, ListItem, Text } from '@chakra-ui/react';
import { NotificationList as NotificationListResponse } from '@diamond/shared/types';
import { Divider } from '@diamond/shared/ui';
import {
  NOTIFICATION_QUERY_KEY,
  putReadNotification,
  useAuthStore,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { ChevronRightOutlined } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import idLocale from 'date-fns/locale/id';
import Link from 'next/link';
import { Fragment, ReactNode, useMemo } from 'react';
import reactStringReplace from 'react-string-replace';

import { parseNotificationRedirect } from './parseNotificationMetadata';

type NotificationListProps = {
  data: NotificationListResponse['data'];
};

export function NotificationList({ data }: NotificationListProps) {
  const queryClient = useQueryClient();
  const access_token = useAuthStore((s) => s.access_token);
  const selectedShipToId = useShipToStore((s) => s.selectedShipToId);

  const mutation = useMutation({
    mutationFn: (id: string) =>
      putReadNotification(access_token, id, {
        warehouse_id: selectedShipToId,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_QUERY_KEY],
      });
    },
  });

  const formatCreatedAt = (createdAt: number) =>
    format(new Date(createdAt), 'eeee, d MMM yyyy', { locale: idLocale });

  const dateHeaders = useMemo(
    () =>
      data.reduce<Record<string, number>>((acc, curr, i) => {
        const dateVal = formatCreatedAt(curr.created_at);
        if (!(dateVal in acc)) {
          acc[dateVal] = i;
        }
        return acc;
      }, {}),
    [data]
  );

  const formatMatchesTextToBold = (
    text: string | Array<ReactNode>,
    regexes: Array<RegExp>
  ) => {
    return regexes.reduce((acc, curr) => {
      acc = reactStringReplace(acc, curr, (match, i) => {
        return (
          <strong key={match + i}>
            <em>{match}</em>
          </strong>
        );
      });
      return acc;
    }, text) as Array<ReactNode>;
  };

  return (
    <List mt="24px">
      {data.map(({ id, title, is_read, body, metadata, created_at }, i) => {
        const href = parseNotificationRedirect(title, metadata);
        const dateVal = formatCreatedAt(created_at);
        const isToday = dateVal === formatCreatedAt(Date.now());

        return (
          <Fragment key={id}>
            {dateHeaders[dateVal] === i ? (
              <Box
                w="full"
                px="5"
                py="2"
                bg="#F2F2F7"
                rounded="lg"
                textAlign="center"
                color="#8E8E93"
                fontSize="sm"
                my="4"
              >
                {dateVal}
              </Box>
            ) : null}
            {i === 0 || dateHeaders[dateVal] === i ? null : <Divider />}
            <ListItem
              py="14px"
              display="flex"
              rounded="lg"
              as={Link}
              onClick={() => !is_read && mutation.mutate(id)}
              href={href}
              _hover={{
                backgroundColor: 'hsl(199 100% 45% / 10%)',
              }}
            >
              <Box px="2">
                <Box
                  w="8px"
                  h="8px"
                  bg={is_read ? 'transparent' : 'blue'}
                  mt="2"
                  rounded="full"
                />
              </Box>
              <Box display="flex" flexDir="column" gap="0.5rem" flex="1">
                <Flex alignItems="center" justifyContent="space-between">
                  <Text
                    fontWeight={is_read ? 'normal' : 'bold'}
                    opacity={is_read ? 0.5 : 1}
                  >
                    {title}
                  </Text>
                  <Text fontSize="sm" color="#8E8E93" pr="5">
                    {isToday ? 'Hari ini, ' : ''}
                    {format(new Date(created_at), 'HH:mm', {
                      locale: idLocale,
                    })}
                  </Text>
                </Flex>
                <Flex alignItems="center" justifyContent="space-between">
                  <Text opacity={is_read ? 0.5 : 1}>
                    {formatMatchesTextToBold(body, [
                      /(SOL\d{9,}[0-9-]*)/g,
                      /(\d{2}\/\d{2}\/\d{4} • \d{2}:\d{2} [A-Z]{3,4})/g,
                    ])}
                  </Text>
                  <Box pr="5">
                    <Icon color="#8E8E93" as={ChevronRightOutlined} />
                  </Box>
                </Flex>
              </Box>
            </ListItem>
          </Fragment>
        );
      })}
    </List>
  );
}
